import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameState'
import { useTimer } from '@/hooks/useTimer'
import {
  useTimerNotification,
  useDecayNotification,
  requestNotificationPermission,
  getNotificationPermission,
  unlockAudio,
} from '@/hooks/useTimerNotification'
import { FOCUS_DURATIONS, type FocusDuration } from '@/data/constants'
import { ConfirmDialog } from './ConfirmDialog'
import styles from './FocusView.module.css'

export function FocusView() {
  const timer = useTimer()
  const stopFocus = useGameStore((s) => s.stopFocus)
  const [showStopConfirm, setShowStopConfirm] = useState(false)

  const handleRequestStop = () => {
    if (!timer.isComplete) {
      setShowStopConfirm(true)
    } else {
      stopFocus()
    }
  }

  const confirmStop = () => {
    stopFocus()
    setShowStopConfirm(false)
  }

  return (
    <div className={styles.container}>
      {timer.isActive ? (
        <ActiveTimer onRequestStop={handleRequestStop} />
      ) : (
        <TimerSelect />
      )}

      {showStopConfirm && (
        <ConfirmDialog
          title="Stop Early?"
          message={
            timer.proRatedEnergy > 0
              ? `You'll receive ${timer.proRatedEnergy} energy for ${Math.floor(timer.elapsedMinutes / 5) * 5} minutes focused. Continue to earn more!`
              : `You need at least 5 minutes to earn energy. Keep focusing!`
          }
          confirmText="Stop Session"
          cancelText="Keep Focusing"
          variant={timer.proRatedEnergy > 0 ? 'default' : 'danger'}
          onConfirm={confirmStop}
          onCancel={() => setShowStopConfirm(false)}
        />
      )}
    </div>
  )
}

function TimerSelect() {
  const startFocus = useGameStore((s) => s.startFocus)
  const activity = useGameStore((s) => s.activity)
  const getEnergy = useGameStore((s) => s.getEnergy)
  const { windowRemaining } = getEnergy()

  const [pendingDuration, setPendingDuration] = useState<FocusDuration | null>(null)
  const [notificationStatus, setNotificationStatus] = useState<NotificationPermission | 'unsupported'>('default')

  useEffect(() => {
    setNotificationStatus(getNotificationPermission())
  }, [])

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission()
    setNotificationStatus(granted ? 'granted' : 'denied')
  }

  const durations: FocusDuration[] = [25, 50, 90]

  const handleStartFocus = (duration: FocusDuration) => {
    if (activity) {
      setPendingDuration(duration)
    } else {
      unlockAudio() // Unlock audio on iOS before timer starts
      startFocus(duration)
    }
  }

  const confirmStartFocus = () => {
    if (pendingDuration) {
      unlockAudio() // Unlock audio on iOS before timer starts
      startFocus(pendingDuration)
      setPendingDuration(null)
    }
  }

  return (
    <div className={`${styles.timerSelect} stagger`}>
      <div className={styles.welcomeText}>
        <h2 className={styles.title}>Begin Your Focus</h2>
        <p className={styles.subtitle}>
          Choose a duration. Energy flows to those who pause.
        </p>
      </div>

      <div className={styles.durationGrid}>
        {durations.map((duration) => {
          const energy = FOCUS_DURATIONS[duration]
          const cappedEnergy = Math.min(energy, windowRemaining)

          return (
            <button
              key={duration}
              className={styles.durationBtn}
              onClick={() => handleStartFocus(duration)}
            >
              <span className={styles.durationTime}>{duration}</span>
              <span className={styles.durationUnit}>min</span>
              <div className={styles.durationEnergy}>
                <span className={styles.energyPlus}>+</span>
                <span>{cappedEnergy}</span>
                <span className={styles.energyIcon}>⚡</span>
                {cappedEnergy < energy && (
                  <span className={styles.energyCapped}>(capped)</span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      <div className={styles.hint}>
        {windowRemaining === 0 ? (
          <p className={styles.exhaustedHint}>
            Energy pool exhausted. Take a break from focusing to restore capacity.
          </p>
        ) : (
          <>
            <p>Energy earned is capped by your 2-hour rolling window.</p>
            <p>Take breaks to restore capacity.</p>
          </>
        )}
      </div>

      {notificationStatus === 'default' && (
        <button
          className={styles.notificationBtn}
          onClick={handleEnableNotifications}
        >
          Enable Timer Notifications
        </button>
      )}

      {pendingDuration && (
        <ConfirmDialog
          title="Cancel Activity?"
          message="Starting a focus session will cancel your current exploration activity. The energy you spent will not be refunded."
          confirmText="Start Focus"
          cancelText="Keep Exploring"
          variant="danger"
          onConfirm={confirmStartFocus}
          onCancel={() => setPendingDuration(null)}
        />
      )}
    </div>
  )
}

interface ActiveTimerProps {
  onRequestStop: () => void
}

function ActiveTimer({ onRequestStop }: ActiveTimerProps) {
  const timer = useTimer()

  // Trigger notifications on timer completion and decay
  useTimerNotification(timer.isComplete, timer.currentEnergy)
  useDecayNotification(timer.isDecaying, timer.currentEnergy)

  const formatTime = (minutes: number) => {
    const m = Math.floor(Math.max(0, minutes))
    const s = Math.floor((Math.max(0, minutes) - m) * 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const progress = timer.isComplete
    ? 1
    : timer.elapsedMinutes / timer.duration

  const ringColor = timer.isDecaying
    ? 'var(--color-danger)'
    : timer.isComplete
    ? 'var(--color-success)'
    : 'var(--color-focus)'

  const ringGradient = timer.isDecaying
    ? `conic-gradient(var(--color-danger) ${(1 - timer.decay / timer.baseEnergy) * 100}%, var(--color-success) 0)`
    : `conic-gradient(${ringColor} ${progress * 100}%, transparent 0)`

  return (
    <div className={`${styles.activeTimer} scale-in`}>
      <div
        className={`${styles.timerRing} ${timer.isComplete ? 'breathe' : ''}`}
        style={{
          '--ring-gradient': ringGradient,
          '--ring-glow': timer.isDecaying
            ? 'var(--color-danger-glow)'
            : timer.isComplete
            ? 'var(--color-success-glow)'
            : 'var(--color-focus-glow)',
        } as React.CSSProperties}
      >
        <div className={styles.timerProgress} />
        <div className={styles.timerInner}>
          {!timer.isComplete ? (
            <>
              <span className={styles.timerTime}>
                {formatTime(timer.remainingMinutes)}
              </span>
              <span className={styles.timerLabel}>remaining</span>
              {timer.proRatedEnergy > 0 && (
                <span className={styles.proRatedEnergy}>
                  <span className={styles.proRatedPlus}>+</span>
                  {timer.proRatedEnergy} ⚡
                </span>
              )}
            </>
          ) : (
            <>
              <span
                className={styles.timerStatus}
                style={{
                  color: timer.isDecaying
                    ? 'var(--color-danger)'
                    : 'var(--color-success)',
                }}
              >
                {timer.isDecaying ? '⚠ DECAYING' : '✓ COMPLETE'}
              </span>
              <span
                className={styles.energyPreview}
                style={{
                  color: timer.isDecaying
                    ? 'var(--color-energy)'
                    : 'var(--color-success)',
                }}
              >
                +{timer.currentEnergy} ⚡
                {timer.isDecaying && (
                  <span className={styles.decayAmount}>(-{timer.decay})</span>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      <button
        className={`${styles.stopBtn} ${timer.isComplete ? styles.stopBtnComplete : ''}`}
        onClick={onRequestStop}
      >
        {timer.isComplete ? 'Collect Energy' : 'Stop Early'}
      </button>

      <p className={styles.timerHint}>
        {timer.isComplete
          ? timer.isInGrace
            ? `Grace period: ${formatTime(timer.graceRemaining)}`
            : timer.isDecaying
            ? 'Stop now to save remaining energy!'
            : ''
          : 'Energy earned per 5 minutes focused'}
      </p>
    </div>
  )
}
